import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { candidateService } from '../services'
import { appName } from '../appConfig'
import { MILESTONE_TYPES, isInterviewMilestone } from '../helpers/milestones'
import { useCandidateDialog } from '../hooks'
import '../index.css'

export const CandidateBoard = () => {
  const navigate = useNavigate()
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [draggedUuid, setDraggedUuid] = useState(null)
  const [dragOverCol, setDragOverCol] = useState(null)

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showBulkAddCandidateDialog, showAddMilestoneDialog } = useCandidateDialog()

  useEffect(() => {
    fetchItems()
  }, [])

  const fetchItems = async () => {
    await candidateService.getAll()
      .then(res => setCandidates(res.candidates || []))
      .catch(err => showMessage(JSON.stringify(err), 'error'))
      .finally(() => setLoading(false))
  }

  const addCandidatesBulk = async (payload) => {
    await candidateService.createBulk(payload)
      .then(res => { hideModalDialog(); fetchItems(); showMessage(`${res.candidates.length} kandidater skapade`+((res.skipped&&res.skipped.length)?`, ${res.skipped.length} hoppades över (saknar e-post/LinkedIn eller redan kandidat)`:``), (res.skipped&&res.skipped.length)?'warning':undefined) })
      .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
  }

  const columns = useMemo(() => {
    return MILESTONE_TYPES.map(m => ({
      ...m,
      candidates: candidates.filter(c => c.current_milestone_type === m.value),
    }))
  }, [candidates])

  const handleDrop = (colValue) => {
    setDragOverCol(null)
    const uuid = draggedUuid
    setDraggedUuid(null)
    if (!uuid) return

    const candidate = candidates.find(c => c.candidate_uuid === uuid)
    if (!candidate || candidate.current_milestone_type === colValue) return

    // An interview column needs a booked slot, which only a form can supply — so this
    // drop opens the milestone dialog (pre-filled with the target stage) instead of
    // posting a stage-only change, and the card doesn't move until the POST resolves.
    // The optimistic-then-rollback path below is right for stages that need no input and
    // wrong for one that opens a dialog: the card would sit in the new column while the
    // dialog is still open, and stay there if it's cancelled.
    if (isInterviewMilestone(colValue)) {
      showAddMilestoneDialog([{ ...candidate, current_milestone_type: colValue }], async (payload) => {
        await candidateService.addMilestone(uuid, payload)
          .then(() => { hideModalDialog(); fetchItems(); showMessage('Intervju inbokad') })
          .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
      })
      return
    }

    // Optimistic move — reconciled with the server response
    setCandidates(prev => prev.map(c => c.candidate_uuid === uuid
      ? { ...c, current_milestone_type: colValue, current_milestone_date: new Date().toISOString() }
      : c))

    candidateService.addMilestone(uuid, { milestone_type: colValue })
      .catch(err => {
        showMessage(JSON.stringify(err), 'error')
        // Roll back on failure
        setCandidates(prev => prev.map(c => c.candidate_uuid === uuid
          ? { ...c, current_milestone_type: candidate.current_milestone_type, current_milestone_date: candidate.current_milestone_date }
          : c))
      })
  }

  if (loading) return <div className="Candidates wide-margin"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  return (
    <div className="Candidates wide-margin">
      <div className="cd-board-header">
        <Button value="+ Ny kandidat" className="button main-button" onClick={() => showBulkAddCandidateDialog(addCandidatesBulk)} />
      </div>

      <div className="cd-board">
        {columns.map(col => (
          <div className="cd-board-col" key={col.value}>
            <div className="cd-board-col-header">
              <span className={`cd-board-col-dot cd-board-col-dot-${col.value}`} />
              <span className="cd-board-col-title">{col.label}</span>
              <span className="cd-board-col-count">({col.candidates.length})</span>
            </div>
            <div
              className={`cd-board-col-body${dragOverCol === col.value ? ' cd-board-col-body-over' : ''}`}
              onDragOver={e => { e.preventDefault(); if (dragOverCol !== col.value) setDragOverCol(col.value) }}
              onDragLeave={() => setDragOverCol(prev => (prev === col.value ? null : prev))}
              onDrop={e => { e.preventDefault(); handleDrop(col.value) }}
            >
              {col.candidates.map(c => (
                <div
                  className={`cd-board-card cd-board-card-${col.value}${draggedUuid === c.candidate_uuid ? ' cd-board-card-dragging' : ''}`}
                  key={c.candidate_uuid}
                  draggable
                  onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDraggedUuid(c.candidate_uuid) }}
                  onDragEnd={() => { setDraggedUuid(null); setDragOverCol(null) }}
                  onClick={() => navigate(`/${appName}/${c.candidate_uuid}`)}
                >
                  <div className="cd-board-card-top">
                    <span className="cd-board-card-name">{c.candidate_name}</span>
                    <span className="cd-board-card-time">{new Date(c.current_milestone_date || c.created_timestamp).toLocaleDateString('sv-SE')}</span>
                  </div>
                  <div className="cd-board-card-owner">{c.owner_name}</div>
                  {/* The next booked interview, straight off v_candidate — independent of
                      the card's column, which shows the round the candidate has reached. */}
                  {c.next_interview_start_timestamp && (
                    <div className="cd-board-card-interview">
                      Intervju {c.next_interview_round} · {new Date(c.next_interview_start_timestamp).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
